import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon, type IconName } from '@/components/Icons';
import { useT } from '@/lib/i18n';
import { useAuthStore } from '@/lib/auth-store';
import { getCertificate, logout, type CertificateStatus } from '@/lib/auth-api';

type StatusVariant = 'success' | 'warning' | 'error' | 'neutral';

export default function DashboardScreen() {
    const t = useT();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const registration = useAuthStore((s) => s.registration);
    const clearAuth = useAuthStore((s) => s.clearAuth);

    const [cert, setCert] = useState<CertificateStatus | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        if (!registration) {
            setLoading(false);
            return;
        }
        getCertificate()
            .then((data) => mounted && setCert(data))
            .catch(() => {
                /* silent */
            })
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, [registration]);

    const handleLogout = async () => {
        try {
            await logout();
        } finally {
            clearAuth();
            navigate('/app/login', { replace: true });
        }
    };

    const status = cert?.statusCode ?? registration?.statusCode ?? null;
    const variant: StatusVariant = statusVariant(status);
    const statusLabel = statusText(status, t);

    return (
        <MobileFrame>
            <div
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '20px 24px 28px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>Halo,</div>
                        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
                            {user?.fullname || '—'}
                        </h1>
                    </div>
                    <button
                        onClick={handleLogout}
                        type="button"
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background: 'var(--ink-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        aria-label="Logout"
                    >
                        <Icon name="logout" size={18} color="var(--ink-700)" />
                    </button>
                </div>

                {/* Status card */}
                <div
                    className="card"
                    style={{
                        padding: 18,
                        marginBottom: 16,
                        background:
                            'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                        color: 'white',
                        border: 'none',
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            opacity: 0.7,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            marginBottom: 6,
                        }}
                    >
                        Status Sertifikat
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            marginBottom: 10,
                        }}
                    >
                        <Icon name="shieldCheck" size={24} color="white" />
                        <div style={{ fontSize: 20, fontWeight: 700 }}>
                            {loading ? 'Memuat…' : statusLabel}
                        </div>
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.5 }}>
                        {registration?.fullName ?? '—'} · NIK{' '}
                        {registration?.nik ? `${registration.nik.slice(0, 6)}…` : '—'}
                    </div>
                    {cert?.certExpiredAt && (
                        <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                            Berlaku hingga {cert.certExpiredAt}
                        </div>
                    )}
                    <VariantBadge variant={variant} />
                </div>

                {/* Shortcut grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <ShortcutCard
                        icon="user"
                        title="Profil"
                        desc="Kelola data pribadi"
                        onClick={() => navigate('/app/profile')}
                    />
                    <ShortcutCard
                        icon="shieldCheck"
                        title="Sertifikat"
                        desc="Cek status CA"
                        onClick={() => navigate('/app/certificate')}
                    />
                    <ShortcutCard
                        icon="signature"
                        title="Tanda Tangan"
                        desc="Perbarui specimen"
                        onClick={() => navigate('/app/specimen')}
                    />
                </div>
            </div>
        </MobileFrame>
    );
}

function ShortcutCard({
    icon,
    title,
    desc,
    onClick,
}: {
    icon: IconName;
    title: string;
    desc: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="card"
            style={{
                textAlign: 'left',
                cursor: 'pointer',
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                transition: 'all 0.15s',
            }}
        >
            <div
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'var(--primary-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Icon name={icon} size={18} color="var(--primary)" />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-900)' }}>{title}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-500)', lineHeight: 1.4 }}>{desc}</div>
        </button>
    );
}

function VariantBadge({ variant }: { variant: StatusVariant }) {
    const map: Record<StatusVariant, { label: string; color: string }> = {
        success: { label: 'Aktif', color: '#3DDC97' },
        warning: { label: 'Menunggu', color: '#FCD34D' },
        error: { label: 'Perhatian', color: '#FCA5A5' },
        neutral: { label: '—', color: 'rgba(255,255,255,0.4)' },
    };
    const v = map[variant];
    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.15)',
                fontSize: 11,
                fontWeight: 600,
                marginTop: 10,
            }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: v.color,
                }}
            />
            {v.label}
        </div>
    );
}

function statusVariant(code: string | null): StatusVariant {
    if (code === 'CERT_ACTIVE') return 'success';
    if (code === 'EKYC_PENDING' || code === 'REGISTERED' || code === 'INITIATED') return 'warning';
    if (code === 'CERT_REJECTED' || code === 'CERT_EXPIRED') return 'error';
    return 'neutral';
}

function statusText(code: string | null, t: ReturnType<typeof useT>): string {
    void t;
    switch (code) {
        case 'CERT_ACTIVE':
            return 'Aktif';
        case 'EKYC_PENDING':
            return 'Menunggu Verifikasi';
        case 'REGISTERED':
            return 'Terdaftar';
        case 'INITIATED':
            return 'Sesi Dimulai';
        case 'CERT_REJECTED':
            return 'Ditolak';
        case 'CERT_EXPIRED':
            return 'Expired';
        default:
            return '—';
    }
}
